import { Avatar, Box, Chip, Tooltip, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { MdCheckCircle, MdOutlineImage, MdWarning, MdCalendarToday, MdTimer, MdAssignment } from 'react-icons/md'
import { BsFileEarmarkText } from 'react-icons/bs'
import { format, parseISO, differenceInDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IKanbanBoardHCPArtesTask } from '../interfaces/kanban-board-hcp-artes-schema.interface'

interface IKanbanCardArteProps {
  task: IKanbanBoardHCPArtesTask
}

export const KanbanCardArte: FC<IKanbanCardArteProps> = ({ task }) => {
  const { content } = task
  const [imageError, setImageError] = useState(false)

  const prazoFormatado = content.prazo_atual
    ? format(parseISO(content.prazo_atual), "dd/MM HH:mm", { locale: ptBR })
    : null

  const dataEntrada = content.criado_em 
    ? format(parseISO(content.criado_em), "dd/MM/yy", { locale: ptBR })
    : null

  const diasNaEtapa = content.atualizado_em
    ? differenceInDays(new Date(), parseISO(content.atualizado_em))
    : 0

  const thumbnailSrc = content.thumbnail_arte_final
    ? content.thumbnail_arte_final.includes('dropbox.com')
      ? content.thumbnail_arte_final.replace('dl=0', 'dl=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      : content.thumbnail_arte_final
    : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

      {/* Cabeçalho do Card: Número + Datas */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight="bold" color="text.primary">
            #{content.numero}
          </Typography>
          {dataEntrada && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
              <MdCalendarToday size={10} color="#757575" />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#757575' }}>
                Entrada: {dataEntrada}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {content.atrasado && (
              <Tooltip title="Atrasado">
                <Box sx={{ display: 'flex' }}>
                  <MdWarning size={16} color="#d32f2f" />
                </Box>
              </Tooltip>
            )}
            {content.no_prazo_producao === false && !content.atrasado && (
              <Tooltip title="Fora do prazo de produção">
                <Box sx={{ display: 'flex' }}>
                  <MdWarning size={14} color="#ed6c02" />
                </Box>
              </Tooltip>
            )}
          </Box>
          
          <Tooltip title={`Na etapa há ${diasNaEtapa} ${diasNaEtapa === 1 ? 'dia' : 'dias'}`}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.3, 
              bgcolor: diasNaEtapa > 5 ? '#fff3e0' : '#f5f7fb',
              px: 0.6,
              py: 0.2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: diasNaEtapa > 5 ? '#ffe0b2' : '#e0e4eb'
            }}>
              <MdTimer size={11} color={diasNaEtapa > 5 ? '#e65100' : '#757575'} />
              <Typography variant="caption" sx={{ 
                fontSize: '0.65rem', 
                fontWeight: 600,
                color: diasNaEtapa > 5 ? '#e65100' : '#757575' 
              }}>
                {diasNaEtapa}d
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Badge de OS — cartela fechada */}
      {content.ordem_de_servico && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <MdAssignment size={12} color="#2e7d32" />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#2e7d32' }}>
            OS #{content.ordem_de_servico}
          </Typography>
        </Box>
      )}

      {/* Nome do cliente */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontWeight: 500 }}
      >
        {content.cliente}
      </Typography>

      {/* Produto */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: '0.70rem' }}
      >
        {content.produto_nome}
      </Typography>

      {/* Tiragem — só exibe se a API retornar o valor */}
      {!!content.tiragem && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.70rem', fontWeight: 500 }}>
          {content.tiragem.toLocaleString('pt-BR')} un
        </Typography>
      )}

      {/* Thumbnail da arte com fallback apenas em caso de erro */}
      {thumbnailSrc && (
        <Box sx={{ mt: 0.5 }}>
          {!imageError ? (
            <Box
              component="img"
              src={thumbnailSrc}
              alt="Thumbnail da arte"
              onError={() => setImageError(true)}
              sx={{
                width: '100%',
                height: 80,
                objectFit: 'contain',
                borderRadius: 1,
                backgroundColor: '#f5f5f5',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 80,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                backgroundColor: '#f5f7fb',
                border: '1px dashed #e0e4eb',
                gap: 0.5,
              }}
            >
              <MdOutlineImage size={24} color="#bdbdbd" />
              <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.60rem' }}>
                Erro ao carregar
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Designer */}
      {content.designer && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Avatar sx={{ width: 18, height: 18, fontSize: '0.65rem', bgcolor: '#84a802' }}>
            {content.designer.nome.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {content.designer.nome}
          </Typography>
        </Box>
      )}

      {/* Ícones de status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Tooltip title={content.tem_briefing ? 'Briefing preenchido' : 'Sem briefing'}>
          <Box sx={{ display: 'flex' }}>
            <BsFileEarmarkText size={13} color={content.tem_briefing ? '#2e7d32' : '#bdbdbd'} />
          </Box>
        </Tooltip>
        <Tooltip title={content.tem_arte ? 'Arte vinculada' : 'Sem arquivo de arte'}>
          <Box sx={{ display: 'flex' }}>
            <MdOutlineImage size={15} color={content.tem_arte ? '#2e7d32' : '#bdbdbd'} />
          </Box>
        </Tooltip>
        {content.link_arte_final && (
          <Tooltip title="Arte final disponível">
            <Box sx={{ display: 'flex' }}>
              <MdCheckCircle size={14} color="#84a802" />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Itens pendentes no pedido */}
      {content.itens_pendentes === 0 ? (
        <Chip
          label="Pedido completo"
          size="small"
          sx={{ fontSize: '0.60rem', height: 18, bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontWeight: 600, alignSelf: 'flex-start' }}
        />
      ) : (
        <Chip
          label={`${content.itens_pendentes} pend. no pedido`}
          size="small"
          sx={{ fontSize: '0.60rem', height: 18, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, alignSelf: 'flex-start' }}
        />
      )}

      {/* Contadores de revisões/alterações */}
      {(content.revisoes_pendentes > 0 || content.alteracoes_entregues > 0) && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {content.revisoes_pendentes > 0 && (
            <Chip
              label={`${content.revisoes_pendentes} rev. pendente${content.revisoes_pendentes > 1 ? 's' : ''}`}
              size="small"
              sx={{ fontSize: '0.60rem', height: 18, bgcolor: '#fff3e0', color: '#e65100' }}
            />
          )}
          {content.alteracoes_entregues > 0 && (
            <Chip
              label={`${content.alteracoes_entregues} alt. entregue${content.alteracoes_entregues > 1 ? 's' : ''}`}
              size="small"
              sx={{ fontSize: '0.60rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32' }}
            />
          )}
        </Box>
      )}

      {/* Etiquetas */}
      {content.etiquetas.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {content.etiquetas.map((etiqueta) => (
            <Chip
              key={etiqueta.id}
              label={etiqueta.nome}
              size="small"
              sx={{ fontSize: '0.60rem', height: 18, bgcolor: etiqueta.cor, color: '#000', fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {/* Prazo */}
      {prazoFormatado && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: content.atrasado ? '#d32f2f' : '#757575',
            fontWeight: content.atrasado ? 600 : 400,
            fontSize: '0.65rem',
          }}
        >
          Prazo: {prazoFormatado}
        </Typography>
      )}

    </Box>
  )
}
